import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Ticket,
  Clock,
  ChevronDown,
  ChevronUp,
  Upload,
  Send,
  Save,
  CalendarDays,
  Inbox,
  LayoutGrid,
  Search,
  MapPin,
  Plane,
  User,
  X,
  CheckCircle2,
  Hourglass,
  Ban,
  LifeBuoy,
} from 'lucide-react';
import AgentLayout from './AgentLayout';
import { useAgentAuth } from '../../context/AgentAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REQ = { withCredentials: true };

const emptyPaxRow = () => ({
  passenger_no: 1,
  title: '',
  first_name: '',
  last_name: '',
  gender: '',
  dob: '',
  nationality: '',
  passport_no: '',
  passport_expiry: '',
});

function deadlineCountdown(iso) {
  if (!iso) return null;
  const raw = typeof iso === 'object' && iso !== null && '$date' in iso ? iso.$date : iso;
  if (!raw) return null;
  let d;
  try {
    const s = String(raw);
    d = new Date(s.includes('T') ? s : `${s}T23:59:59Z`);
  } catch {
    return null;
  }
  const now = Date.now();
  const ms = d.getTime() - now;
  if (Number.isNaN(ms)) return null;
  if (ms <= 0) return 'Deadline passed';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h >= 48) return `${Math.floor(h / 24)}d ${h % 24}h left`;
  if (h >= 1) return `${h}h ${m}m left`;
  return `${m}m left`;
}

function formatSchedule(o) {
  const parts = [];
  if (o.departure_time) parts.push(`Dep ${o.departure_time}`);
  if (o.arrival_time) parts.push(`Arr ${o.arrival_time}`);
  if (o.duration) parts.push(String(o.duration));
  if (o.schedule_type) parts.push(String(o.schedule_type));
  return parts.join(' · ') || 'Schedule on file';
}

const tabBtn = (active) =>
  `flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
    active
      ? 'bg-white text-[#1e3a5f] shadow-md'
      : 'text-teal-100/90 hover:bg-white/10 hover:text-white'
  }`;

const AgentSeatBlocks = () => {
  const { agent } = useAgentAuth() || {};
  const [tab, setTab] = useState('catalog');

  const [blocks, setBlocks] = useState([]);
  const [requests, setRequests] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [catalogSearch, setCatalogSearch] = useState('');

  const [loading, setLoading] = useState(true);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [requestsLoading, setRequestsLoading] = useState(false);

  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [paxDraft, setPaxDraft] = useState([]);
  const [csvText, setCsvText] = useState('');
  const [saving, setSaving] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);

  const [requestModal, setRequestModal] = useState(null);
  const [reqSeats, setReqSeats] = useState(1);
  const [reqRemarks, setReqRemarks] = useState('');
  const [reqSubmitting, setReqSubmitting] = useState(false);

  const loadBlocks = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/agent/seat-blocks`, REQ);
      if (r.data?.success) setBlocks(r.data.data || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/agent/seat-blocks/requests`, REQ);
      if (r.data?.success) setRequests(r.data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setRequestsLoading(false);
    }
  }, []);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/agent/seat-blocks/catalog`, REQ);
      if (r.data?.success) setCatalog(r.data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await loadBlocks();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadBlocks]);

  useEffect(() => {
    if (tab === 'catalog') loadCatalog();
  }, [tab, loadCatalog]);

  useEffect(() => {
    if (tab === 'requests') loadRequests();
  }, [tab, loadRequests]);

  const pendingCount = useMemo(() => requests.filter((x) => x.status === 'pending').length, [requests]);

  const filteredCatalog = useMemo(() => {
    const q = catalogSearch.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter((o) => {
      const id = (o.offline_flight_id || '').toLowerCase();
      const fn = (o.flight_number || '').toLowerCase();
      const al = (o.airline_name || '').toLowerCase();
      const r = o.route || {};
      const cities = `${r.from_city || ''} ${r.to_city || ''} ${r.from_airport || ''} ${r.to_airport || ''}`.toLowerCase();
      const dt = (o.departure_date || '').toLowerCase();
      return id.includes(q) || fn.includes(q) || al.includes(q) || cities.includes(q) || dt.includes(q);
    });
  }, [catalog, catalogSearch]);

  const fetchBlockDetail = async (blockId) => {
    setDetailLoading(true);
    setCsvText('');
    try {
      const r = await axios.get(`${API_URL}/api/agent/seat-blocks/${blockId}`, REQ);
      if (r.data?.success) {
        const b = r.data.data;
        setDetail(b);
        const rows = (b.passengers || []).map((p) => ({
          passenger_no: p.passenger_no,
          title: p.title || '',
          first_name: p.first_name || '',
          last_name: p.last_name || '',
          gender: p.gender || '',
          dob: p.dob || '',
          nationality: p.nationality || '',
          passport_no: p.passport_no || '',
          passport_expiry: p.passport_expiry || '',
          passenger_status: p.passenger_status,
          display_name: p.display_name,
        }));
        setPaxDraft(rows.length ? rows : [emptyPaxRow()]);
      }
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Could not load block');
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleRow = (blockId) => {
    if (expandedId === blockId) {
      setExpandedId(null);
      setDetail(null);
      setPaxDraft([]);
      return;
    }
    setExpandedId(blockId);
    fetchBlockDetail(blockId);
  };

  const updatePaxField = (idx, field, value) => {
    setPaxDraft((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const savePassengers = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const passengers = paxDraft.map((p) => ({
        passenger_no: p.passenger_no,
        title: p.title || undefined,
        first_name: p.first_name || undefined,
        last_name: p.last_name || undefined,
        gender: p.gender || undefined,
        dob: p.dob || undefined,
        nationality: p.nationality || undefined,
        passport_no: p.passport_no || undefined,
        passport_expiry: p.passport_expiry || undefined,
      }));
      const r = await axios.put(
        `${API_URL}/api/agent/seat-blocks/${detail.id}/passengers`,
        { passengers },
        REQ
      );
      if (r.data?.success) {
        alert(`Saved (${r.data.status || 'ok'})`);
        await fetchBlockDetail(detail.id);
        loadBlocks();
      }
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runBulkCsv = async () => {
    if (!detail || !csvText.trim()) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/agent/seat-blocks/${detail.id}/bulk-upload`,
        { csv_text: csvText },
        REQ
      );
      if (r.data?.success) {
        alert('CSV applied');
        setCsvText('');
        await fetchBlockDetail(detail.id);
        loadBlocks();
      }
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const finalSubmit = async () => {
    if (!detail) return;
    if (!window.confirm('Submit final passenger names?')) return;
    setSubmitBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/agent/seat-blocks/${detail.id}/final-submit`, {}, REQ);
      if (r.data?.success) {
        alert('Submitted');
        await fetchBlockDetail(detail.id);
        loadBlocks();
      }
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Final submit failed');
    } finally {
      setSubmitBusy(false);
    }
  };

  const openRequestModal = (offer) => {
    setRequestModal(offer);
    const maxSeats = Math.min(50, Math.max(1, Number(offer.seats_available) || 1));
    setReqSeats(maxSeats > 0 ? 1 : 0);
    setReqRemarks('');
  };

  const maxRequestSeats = requestModal
    ? Math.min(50, Math.max(0, Number(requestModal.seats_available) || 0))
    : 50;

  const submitBlockRequest = async () => {
    if (!requestModal) return;
    setReqSubmitting(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/agent/seat-blocks/requests`,
        {
          offline_flight_id: requestModal.offline_flight_id,
          departure_date: requestModal.departure_date,
          seats: Math.min(maxRequestSeats, Math.max(1, Number(reqSeats) || 1)),
          remarks: reqRemarks.trim() || undefined,
        },
        REQ
      );
      if (r.data?.success) {
        alert('Request sent. An administrator will confirm before your wallet is debited.');
        setRequestModal(null);
        loadRequests();
        setTab('requests');
      }
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Could not submit request');
    } finally {
      setReqSubmitting(false);
    }
  };

  const cancelMyRequest = async (requestId) => {
    if (!window.confirm('Cancel this pending request?')) return;
    try {
      await axios.post(`${API_URL}/api/agent/seat-blocks/requests/${requestId}/cancel`, {}, REQ);
      loadRequests();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Could not cancel');
    }
  };

  const countdown = deadlineCountdown(detail?.name_deadline_at);

  if (!agent) {
    return (
      <AgentLayout>
        <p className="text-gray-600">Please sign in as an agent.</p>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="max-w-6xl mx-auto space-y-8 pb-10">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1e3a5f] via-[#1B375B] to-teal-800 p-8 shadow-xl text-white">
          <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-teal-400/20 blur-2xl" />
          <div className="absolute -left-10 bottom-0 h-40 w-40 rounded-full bg-cyan-300/10 blur-2xl" />
          <div className="relative flex flex-col gap-4">
            <div>
              <p className="text-teal-200/90 text-sm font-medium uppercase tracking-wide">Offline inventory</p>
              <h1 className="mt-1 text-3xl font-bold flex items-center gap-3">
                <span className="rounded-xl bg-white/10 p-2">
                  <Ticket className="h-8 w-8 text-teal-300" />
                </span>
                My blocked seats
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-teal-50/95 leading-relaxed">
                Browse published block dates and fares (no live seat counts), request a block for admin approval, then
                add passenger names here after the block is confirmed.
              </p>
              <Link
                to="/agent/support-tickets"
                className="inline-flex items-center gap-2 mt-3 text-sm font-semibold text-teal-100 hover:text-white underline-offset-2 hover:underline"
                data-testid="seat-blocks-support-link"
              >
                <LifeBuoy className="w-4 h-4" /> Need help with seat blocks?
              </Link>
            </div>
          </div>

          {/* Tabs */}
          <div className="relative mt-8 flex flex-wrap gap-2 rounded-2xl bg-black/20 p-1.5 border border-white/10">
            <button type="button" className={tabBtn(tab === 'catalog')} onClick={() => setTab('catalog')}>
              <CalendarDays className="h-4 w-4" />
              Browse &amp; request
            </button>
            <button type="button" className={tabBtn(tab === 'requests')} onClick={() => setTab('requests')}>
              <Inbox className="h-4 w-4" />
              My requests
              {pendingCount > 0 && (
                <span className="ml-1 rounded-full bg-amber-400 px-2 py-0.5 text-xs font-bold text-[#1B375B]">
                  {pendingCount}
                </span>
              )}
            </button>
            <button type="button" className={tabBtn(tab === 'blocks')} onClick={() => setTab('blocks')}>
              <LayoutGrid className="h-4 w-4" />
              Active blocks
              {!loading && blocks.length > 0 && (
                <span className="ml-1 rounded-full bg-teal-400/30 px-2 py-0.5 text-xs">{blocks.length}</span>
              )}
            </button>
          </div>
        </div>

        {/* Catalog */}
        {tab === 'catalog' && (
          <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-lg font-bold text-gray-900">Available block dates</h2>
              <div className="relative max-w-md w-full">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  placeholder="Search city, airport, flight no., date…"
                  value={catalogSearch}
                  onChange={(e) => setCatalogSearch(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm shadow-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                />
              </div>
            </div>
            {catalogLoading ? (
              <div className="flex justify-center py-20">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-teal-500 border-t-transparent" />
              </div>
            ) : filteredCatalog.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/80 p-12 text-center text-gray-500">
                No published offers match your search. Try clearing filters or check back later.
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {filteredCatalog.map((o) => {
                  const r = o.route || {};
                  return (
                    <div
                      key={`${o.offline_flight_id}-${o.departure_date}`}
                      className="group rounded-2xl border border-teal-100 bg-white p-5 shadow-sm transition hover:border-teal-300 hover:shadow-md"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-teal-600">
                            {o.airline_name} · {o.flight_number}
                          </p>
                          <p className="mt-1 flex items-center gap-1 text-sm font-semibold text-gray-900">
                            <MapPin className="h-4 w-4 text-teal-500 shrink-0" />
                            {r.from_airport} → {r.to_airport}
                            <span className="font-normal text-gray-500">
                              ({r.from_city || '—'} – {r.to_city || '—'})
                            </span>
                          </p>
                        </div>
                        <span className="rounded-lg bg-teal-50 px-2 py-1 text-xs font-mono text-teal-800">
                          {o.offline_flight_id}
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                        <span className="inline-flex items-center gap-1 rounded-lg bg-gray-50 px-2 py-1 font-medium text-gray-800">
                          <CalendarDays className="h-4 w-4 text-teal-600" />
                          {o.departure_date}
                          {o.day_of_week ? ` · ${o.day_of_week}` : ''}
                        </span>
                        {o.journey_scope && (
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs capitalize">{o.journey_scope}</span>
                        )}
                        {o.flight_type && (
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs capitalize">{o.flight_type}</span>
                        )}
                      </div>
                      <p className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                        <Plane className="h-4 w-4 shrink-0 text-gray-400" />
                        {formatSchedule(o)}
                      </p>
                      <div className="mt-4 flex items-end justify-between border-t border-gray-100 pt-4">
                        <div>
                          <p className="text-xs text-gray-500">Indicative fare / seat (adult)</p>
                          <p className="text-2xl font-bold text-[#1B375B]">₹{Number(o.fare_per_seat).toLocaleString('en-IN')}</p>
                          <p className="text-xs text-teal-700 mt-1 font-medium">
                            {Number(o.seats_available) > 0
                              ? `${o.seats_available} seat${o.seats_available === 1 ? '' : 's'} available to block`
                              : 'No seats available'}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">Final amount set when admin confirms.</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => openRequestModal(o)}
                          disabled={!Number(o.seats_available)}
                          className="rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:from-teal-600 hover:to-cyan-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Request block
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* My requests */}
        {tab === 'requests' && (
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-teal-50/40 px-6 py-4">
              <h2 className="text-lg font-bold text-gray-900">My block requests</h2>
              <p className="text-sm text-gray-600 mt-1">Pending rows wait for admin approval before your wallet is charged.</p>
            </div>
            {requestsLoading ? (
              <div className="flex justify-center py-16">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-teal-500 border-t-transparent" />
              </div>
            ) : requests.length === 0 ? (
              <div className="p-10 text-center text-gray-500">You have not submitted any requests yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3">ID</th>
                      <th className="px-4 py-3">Flight</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Seats</th>
                      <th className="px-4 py-3">Est. total</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {requests.map((rq) => (
                      <tr key={rq.id} className="hover:bg-teal-50/30">
                        <td className="px-4 py-3 font-mono text-xs text-gray-600">{rq.id}</td>
                        <td className="px-4 py-3 font-medium text-gray-900">{rq.offline_flight_id}</td>
                        <td className="px-4 py-3">{rq.departure_date}</td>
                        <td className="px-4 py-3">{rq.seats}</td>
                        <td className="px-4 py-3 font-semibold text-[#1B375B]">₹{rq.total_amount}</td>
                        <td className="px-4 py-3">
                          <StatusChip status={rq.status} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          {rq.status === 'pending' && (
                            <button
                              type="button"
                              onClick={() => cancelMyRequest(rq.id)}
                              className="text-xs font-semibold text-red-600 hover:underline"
                            >
                              Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Active blocks */}
        {tab === 'blocks' && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-900">Confirmed seat blocks</h2>
            {loading ? (
              <div className="flex justify-center py-16">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-teal-500 border-t-transparent" />
              </div>
            ) : blocks.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-10 text-center text-gray-500">
                No active blocks yet. Use <strong>Browse &amp; request</strong> to ask your admin for a block.
              </div>
            ) : (
              <div className="space-y-3">
                {blocks.map((b) => (
                  <div
                    key={b.id}
                    className="overflow-hidden rounded-2xl border border-teal-100 bg-white shadow-sm transition hover:shadow-md"
                  >
                    <button
                      type="button"
                      onClick={() => toggleRow(b.id)}
                      className="flex w-full items-center justify-between px-5 py-4 text-left hover:bg-teal-50/40"
                    >
                      <div>
                        <p className="font-mono text-sm font-semibold text-teal-700">{b.block_code}</p>
                        <p className="mt-1 text-sm text-gray-700">
                          <span className="font-medium">{b.offline_flight_id}</span>
                          <span className="mx-2 text-gray-300">|</span>
                          {b.departure_date}
                          <span className="mx-2 text-gray-300">|</span>
                          {b.seats_blocked} seats
                          <span className="mx-2 text-gray-300">|</span>
                          <span className="font-semibold text-[#1B375B]">₹{b.total_amount}</span>
                        </p>
                        <p className="mt-1 text-xs text-gray-500">Status: {b.status}</p>
                      </div>
                      {expandedId === b.id ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </button>

                    {expandedId === b.id && (
                      <div className="border-t border-teal-100 bg-gradient-to-b from-gray-50/80 to-white px-5 py-5">
                        {detailLoading ? (
                          <p className="text-sm text-gray-500">Loading…</p>
                        ) : detail ? (
                          <div className="space-y-5">
                            {countdown && (
                              <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                                <Clock className="h-4 w-4 shrink-0" />
                                <span>Name deadline: {countdown}</span>
                              </div>
                            )}

                            <div>
                              <h3 className="text-base font-bold text-gray-900">Passengers</h3>
                              <p className="mt-1 text-sm text-gray-600">
                                Names must match travel documents. For international flights, passport details are required before you can final-submit.
                              </p>
                              <div className="mt-4 space-y-4">
                                {paxDraft.map((p, idx) => (
                                  <div
                                    key={p.passenger_no}
                                    className="rounded-xl border border-teal-100 bg-white p-4 sm:p-5 shadow-sm"
                                  >
                                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-3 mb-4">
                                      <div className="flex items-center gap-2">
                                        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-700">
                                          <User className="h-5 w-5" />
                                        </span>
                                        <span className="font-semibold text-[#1B375B]">Passenger {p.passenger_no}</span>
                                      </div>
                                      <span
                                        className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                                          p.passenger_status === 'NAME_PENDING'
                                            ? 'bg-amber-100 text-amber-900'
                                            : 'bg-teal-100 text-teal-900'
                                        }`}
                                      >
                                        {p.passenger_status === 'NAME_PENDING' ? 'TBA — add names' : 'In progress'}
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                      <div>
                                        <label className="text-xs font-medium text-gray-600">Title</label>
                                        <select
                                          className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                                          value={p.title}
                                          onChange={(e) => updatePaxField(idx, 'title', e.target.value)}
                                        >
                                          <option value="">Select</option>
                                          <option value="Mr">Mr</option>
                                          <option value="Mrs">Mrs</option>
                                          <option value="Ms">Ms</option>
                                          <option value="Miss">Miss</option>
                                          <option value="Mstr">Mstr</option>
                                          <option value="Dr">Dr</option>
                                        </select>
                                      </div>
                                      <div>
                                        <label className="text-xs font-medium text-gray-600">First name</label>
                                        <input
                                          className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                                          value={p.first_name}
                                          onChange={(e) => updatePaxField(idx, 'first_name', e.target.value)}
                                          placeholder="Given name"
                                        />
                                      </div>
                                      <div>
                                        <label className="text-xs font-medium text-gray-600">Last name</label>
                                        <input
                                          className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                                          value={p.last_name}
                                          onChange={(e) => updatePaxField(idx, 'last_name', e.target.value)}
                                          placeholder="Surname"
                                        />
                                      </div>
                                      <div>
                                        <label className="text-xs font-medium text-gray-600">Gender</label>
                                        <select
                                          className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-teal-500 outline-none"
                                          value={p.gender}
                                          onChange={(e) => updatePaxField(idx, 'gender', e.target.value)}
                                        >
                                          <option value="">Select</option>
                                          <option value="M">Male</option>
                                          <option value="F">Female</option>
                                          <option value="Other">Other</option>
                                        </select>
                                      </div>
                                      <div>
                                        <label className="text-xs font-medium text-gray-600">Date of birth</label>
                                        <input
                                          type="date"
                                          className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-500 outline-none"
                                          value={
                                            typeof p.dob === 'string' && /^\d{4}-\d{2}-\d{2}/.test(p.dob)
                                              ? p.dob.slice(0, 10)
                                              : ''
                                          }
                                          onChange={(e) => updatePaxField(idx, 'dob', e.target.value)}
                                        />
                                      </div>
                                      <div>
                                        <label className="text-xs font-medium text-gray-600">Nationality</label>
                                        <input
                                          className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-500 outline-none"
                                          value={p.nationality}
                                          onChange={(e) => updatePaxField(idx, 'nationality', e.target.value)}
                                          placeholder="e.g. IN"
                                        />
                                      </div>
                                      <div className="sm:col-span-2 lg:col-span-3 rounded-lg bg-slate-50/80 p-4 border border-slate-100">
                                        <p className="text-xs font-semibold text-slate-700 mb-3">Passport (international)</p>
                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                          <div>
                                            <label className="text-xs font-medium text-gray-600">Passport number</label>
                                            <input
                                              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-teal-500 outline-none"
                                              value={p.passport_no}
                                              onChange={(e) => updatePaxField(idx, 'passport_no', e.target.value)}
                                              placeholder="As on passport"
                                            />
                                          </div>
                                          <div>
                                            <label className="text-xs font-medium text-gray-600">Passport expiry</label>
                                            <input
                                              type="date"
                                              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-teal-500 outline-none"
                                              value={
                                                typeof p.passport_expiry === 'string' &&
                                                /^\d{4}-\d{2}-\d{2}/.test(p.passport_expiry)
                                                  ? p.passport_expiry.slice(0, 10)
                                                  : ''
                                              }
                                              onChange={(e) => updatePaxField(idx, 'passport_expiry', e.target.value)}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={savePassengers}
                                disabled={saving}
                                className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 disabled:opacity-50"
                              >
                                <Save className="h-4 w-4" />
                                {saving ? 'Saving…' : 'Save draft'}
                              </button>
                              <button
                                type="button"
                                onClick={finalSubmit}
                                disabled={submitBusy || detail.status === 'TICKETED'}
                                className="inline-flex items-center gap-2 rounded-xl bg-[#1e3a5f] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#152a45] disabled:opacity-50"
                              >
                                <Send className="h-4 w-4" />
                                {submitBusy ? 'Submitting…' : 'Final submit'}
                              </button>
                            </div>

                            <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 p-4">
                              <p className="text-xs font-semibold text-gray-600">
                                Bulk CSV — passenger_no,first_name,last_name,gender,dob,nationality,passport_no,passport_expiry
                              </p>
                              <textarea
                                className="mt-2 w-full min-h-[88px] rounded-lg border border-gray-200 bg-white p-2 font-mono text-xs focus:border-teal-500 outline-none"
                                placeholder="1,Jane,Doe,F,1990-01-15,IN,,"
                                value={csvText}
                                onChange={(e) => setCsvText(e.target.value)}
                              />
                              <button
                                type="button"
                                onClick={runBulkCsv}
                                disabled={bulkBusy || !csvText.trim()}
                                className="mt-2 inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                              >
                                <Upload className="h-4 w-4" />
                                {bulkBusy ? 'Applying…' : 'Apply CSV'}
                              </button>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Request modal */}
        {requestModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-lg font-bold text-[#1B375B]">Request seat block</h3>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => setRequestModal(null)}
                  className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                {requestModal.airline_name} {requestModal.flight_number} · {requestModal.departure_date} ·{' '}
                {(requestModal.route || {}).from_airport} → {(requestModal.route || {}).to_airport}
              </p>
              <p className="mt-3 text-sm text-gray-500">
                Indicative <strong>₹{Number(requestModal.fare_per_seat).toLocaleString('en-IN')}</strong> per seat (adult).
                Up to <strong>{maxRequestSeats}</strong> seat{maxRequestSeats === 1 ? '' : 's'} available for this date.
                Your wallet is <strong>not</strong> debited until an administrator approves.
              </p>
              <label className="mt-4 block text-sm font-medium text-gray-700">Number of seats</label>
              <input
                type="number"
                min={1}
                max={maxRequestSeats || 1}
                disabled={maxRequestSeats < 1}
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none disabled:bg-gray-50"
                value={reqSeats}
                onChange={(e) =>
                  setReqSeats(Math.min(maxRequestSeats || 1, Math.max(1, parseInt(e.target.value, 10) || 1)))
                }
              />
              <label className="mt-4 block text-sm font-medium text-gray-700">Note to admin (optional)</label>
              <textarea
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-teal-500 outline-none"
                rows={3}
                value={reqRemarks}
                onChange={(e) => setReqRemarks(e.target.value)}
                placeholder="e.g. Client names expected by Friday…"
              />
              <p className="mt-2 text-xs text-gray-500">
                Estimated total if approved:{' '}
                <strong>
                  ₹{(Number(requestModal.fare_per_seat) * Number(reqSeats || 1)).toLocaleString('en-IN')}
                </strong>
              </p>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setRequestModal(null)}
                  className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={reqSubmitting}
                  onClick={submitBlockRequest}
                  className="rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md hover:from-teal-600 hover:to-cyan-700 disabled:opacity-50"
                >
                  {reqSubmitting ? 'Sending…' : 'Send request'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AgentLayout>
  );
};

function StatusChip({ status }) {
  const s = (status || '').toLowerCase();
  if (s === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-900">
        <Hourglass className="h-3.5 w-3.5" /> Pending
      </span>
    );
  }
  if (s === 'approved') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
        <CheckCircle2 className="h-3.5 w-3.5" /> Approved
      </span>
    );
  }
  if (s === 'rejected') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800">
        <Ban className="h-3.5 w-3.5" /> Rejected
      </span>
    );
  }
  if (s === 'cancelled') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">
        Cancelled
      </span>
    );
  }
  return <span className="text-xs text-gray-600">{status}</span>;
}

export default AgentSeatBlocks;
